'use client'

import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { deleteCookie } from '@/helpers/deleteCookie'
import { getCookie } from '@/helpers/getCookie'
import { setCookie } from '@/helpers/setCookie'

const listeners = new Map<string, Set<() => void>>()

function subscribe(cookieName: string, callback: () => void) {
  if (!listeners.has(cookieName)) {
    listeners.set(cookieName, new Set())
  }
  listeners.get(cookieName)?.add(callback)

  return () => {
    listeners.get(cookieName)?.delete(callback)
    if (listeners.get(cookieName)?.size === 0) {
      listeners.delete(cookieName)
    }
  }
}

function notify(cookieName: string) {
  listeners.get(cookieName)?.forEach((callback) => callback())
}

export function setCookieAndNotify(name: string, value: string, days?: number): boolean {
  const isNewValue = getCookie(name) !== value
  const success = setCookie(name, value, days)

  if (success && isNewValue) {
    notify(name)
  }

  return success
}

export function deleteCookieAndNotify(name: string): void {
  const hadValue = getCookie(name) !== null

  deleteCookie(name)

  if (hadValue) {
    notify(name)
  }
}

export function useCookie<T extends string>(cookieName: string, defaultValue: T, serverCookies?: string) {
  const getSnapshot = useCallback(() => {
    try {
      return getCookie(cookieName) as T
    } catch (error) {
      console.error(`Failed to get cookie ${cookieName}:`, error)

      return null
    }
  }, [cookieName])

  const subscribeToCookie = useCallback((callback: () => void) => subscribe(cookieName, callback), [cookieName])

  const getServerSnapshot = useCallback(() => {
    try {
      return (getCookie(cookieName, serverCookies) as T) ?? defaultValue
    } catch (error) {
      console.error(`Failed to get server cookie ${cookieName}:`, error)

      return defaultValue
    }
  }, [cookieName, serverCookies, defaultValue])

  const value = useSyncExternalStore(subscribeToCookie, getSnapshot, getServerSnapshot)

  const setCookieValue = useCallback(
    (newValue: T, days?: number) => {
      setCookieAndNotify(cookieName, newValue, days)
    },
    [cookieName]
  )

  const isSet = value !== null

  return useMemo(
    () => [value ?? defaultValue, setCookieValue, isSet] as const,
    [value, defaultValue, setCookieValue, isSet]
  )
}
